import { DOCUMENT } from '@angular/common'
import {
	afterNextRender,
	DestroyRef,
	ElementRef,
	inject,
	Injectable,
	InjectionToken,
	Injector,
	NgZone,
} from '@angular/core'
import { Router } from '@angular/router'

import { Bugfender, LogLevel } from '@bugfender/sdk'

import { environment } from '@env'
import { PaintWatch } from '@definitions/paint-watch'
import { PaymentResultLog } from '@definitions/payment-result-log'
import { LogFilterWord } from '@enums/log-filter-word'
import { LoggingService } from '@enums/logging'
import { PaintVerdict } from '@enums/paint-verdict'
import { PaymentGateway } from '@enums/payment-gateway'
import { ConsoleLoggerService } from '@services/console-logger-service'
import {
	BURST_WINDOW_MS,
	isUnpainted,
	logValue,
	NAVIGATION_STUCK_MS,
	NO_TICKET_TAG,
	PAINT_CHECKS_MS,
	paintVerdict,
	ticketLogTag,
	withinBurstCap,
	yesNo,
} from '@utils/transition-log'

export interface TransitionLogTiming {
	/** The painted checks, in ms after the callback. */
	readonly paintChecksMs: readonly number[]
	readonly navigationStuckMs: number
}

/** Replaced by specs so they do not wait seconds. */
export const TRANSITION_LOG_TIMING = new InjectionToken<TransitionLogTiming>('TRANSITION_LOG_TIMING', {
	providedIn: 'root',
	factory: () => ({ paintChecksMs: PAINT_CHECKS_MS, navigationStuckMs: NAVIGATION_STUCK_MS }),
})

/** Whether the guest's tab is visible; replaced by specs to simulate a locked phone. */
export const PAGE_VISIBLE = new InjectionToken<() => boolean>('PAGE_VISIBLE', {
	providedIn: 'root',
	factory: () => {
		const document = inject(DOCUMENT)

		return () => document.visibilityState !== 'hidden'
	},
})

/** What the payment component shows until an approved charge closes its dialog. */
const PAYMENT_DIALOG_OPEN = 'payment dialog'
const PAYMENT_DIALOG_CLOSED = 'closed'

interface PaintOutcome {
	readonly verdict: PaintVerdict
	readonly shown: string | null
	readonly ms: number
	/** Visible both when the callback ran and when the window concluded. */
	readonly visible: boolean
}

/**
 * Lines of the guest journey (ticket opened → pay or request the car → live status) that tell whether what a
 * third-party callback delivered (Firebase, a payment SDK, a router navigation) reached the guest's screen.
 * They are the baseline to compare against after each Angular upgrade, the top risk of which is a screen that
 * stops refreshing after such a callback.
 *
 * Every line carries the ticket's tag and `+<ms>` since the page loaded. Sending and the painted timers run
 * outside Angular's zone, so logging never triggers change detection (which would hide the very regression it
 * looks for), and a logging failure is swallowed: it never breaks the guest's screen.
 */
@Injectable({
	providedIn: 'root',
})
export class TransitionLogService {
	readonly #zone = inject(NgZone)
	readonly #router = inject(Router)
	readonly #consoleLogger = inject(ConsoleLoggerService)
	readonly #timing = inject(TRANSITION_LOG_TIMING)
	readonly #pageVisible = inject(PAGE_VISIBLE)

	#tag = NO_TICKET_TAG
	#sentAt: readonly number[] = []
	// A window or navigation timer that outlives the app (a spec's injector) sends nothing.
	#destroyed = false

	constructor() {
		inject(DestroyRef).onDestroy(() => {
			this.#destroyed = true
		})
	}

	/** Tags every following line with this ticket; `no-ticket` until the ticket info arrives. */
	setTicket(ticketUuid: string | null | undefined): void {
		this.#tag = ticketLogTag(ticketUuid)
	}

	/** A routine line, dropped beyond the burst cap. */
	info(text: string): void {
		this.#send(LogLevel.Info, text, { capped: true })
	}

	/** An info line the burst cap never drops: a guest retrying cards must leave every decline. */
	infoUncapped(text: string): void {
		this.#send(LogLevel.Info, text, { capped: false })
	}

	/** A warning, always sent: each one closes a window that logs a single line. */
	warn(text: string): void {
		this.#send(LogLevel.Warning, text, { capped: false })
	}

	/**
	 * Watches whether the screen shows what a callback delivered: once after the next render, then at each
	 * painted check, reading the DOM rather than signals, so a screen that never re-rendered is still read.
	 * Logs one line per window, the first conclusive one; returns what closes the window without a line (the
	 * screen was destroyed, or a newer update replaced the window).
	 */
	watchPaint(watch: PaintWatch): () => void {
		const paintWindow = new PaintWindow(watch, this.#timing.paintChecksMs, this.#pageVisible, outcome =>
			this.#logPaint(watch, outcome)
		)

		try {
			afterNextRender(() => paintWindow.sample(), { injector: watch.injector })
			this.#zone.runOutsideAngular(() => paintWindow.schedule())
		} catch {
			paintWindow.close()
		}

		return () => paintWindow.close()
	}

	/**
	 * Warns when a navigation started from a gateway callback is still where it started after
	 * `NAVIGATION_STUCK_MS`: neither at its URL nor moved anywhere else (the guest tapping on to another screen is
	 * no failure). Reads the router's URL instead of the navigation's promise, so the promise and its rejection
	 * reach the app exactly as before.
	 */
	watchNavigation(label: string, url: string, inAngularZone: boolean): void {
		const startedAt = performance.now()
		const startUrl = this.#router.url

		this.#zone.runOutsideAngular(() =>
			setTimeout(() => {
				const currentUrl = this.#router.url
				if (currentUrl.startsWith(url) || currentUrl !== startUrl) return

				this.warn(
					`Callback navigation stuck · target ${label} · zone ${yesNo(inAngularZone)} · after ${Math.round(performance.now() - startedAt)}ms · ${LogFilterWord.CallbackNavigation}`
				)
			}, this.#timing.navigationStuckMs)
		)
	}

	#logPaint({ line, filterWord }: PaintWatch, { verdict, shown, ms, visible }: PaintOutcome): void {
		const visibility = `visible ${yesNo(visible)}`
		const notPainted = `${line} · screen shows ${logValue(shown)} after ${ms}ms · ${visibility} · ${verdict} · ${filterWord}`

		if (verdict === PaintVerdict.Stuck) {
			this.warn(notPainted)

			return
		}

		if (verdict === PaintVerdict.Hidden) {
			this.info(notPainted)

			return
		}

		this.info(`${line} · ${verdict} ${ms}ms · ${visibility} · ${filterWord}`)
	}

	#send(level: LogLevel, text: string, { capped }: { readonly capped: boolean }): void {
		try {
			if (this.#destroyed) return

			const now = performance.now()
			if (capped && !withinBurstCap(this.#sentAt, now)) return

			this.#sentAt = [...this.#sentAt.filter(at => now - at < BURST_WINDOW_MS), now]
			const line = `${text} · +${Math.round(now)}ms`

			this.#zone.runOutsideAngular(() => this.#write(level, line))
		} catch {
			// Logging never breaks the guest's screen.
		}
	}

	#write(level: LogLevel, text: string): void {
		if (environment.logging.service === LoggingService.Console) {
			const write = level === LogLevel.Warning ? this.#consoleLogger.warn : this.#consoleLogger.info

			write.call(this.#consoleLogger, text, { tag: this.#tag })

			return
		}

		Bugfender.sendLog({ level, text, tag: this.#tag })
	}
}

/**
 * Called in a payment component's field initializer. An approval tells whether the charge closed the payment
 * dialog (the component's element leaving the page is the screen moving on); a decline names the gateway's code,
 * so "my card would not go through" has a line.
 */
export const injectPaymentResultLog = (gateway: PaymentGateway): PaymentResultLog => {
	const transitionLog = inject(TransitionLogService)
	const injector = inject(Injector)
	const host: HTMLElement = inject(ElementRef).nativeElement

	const declined = (code: unknown): void =>
		transitionLog.infoUncapped(
			`Payment result · gateway ${gateway} · approved no · code ${logValue(code)} · ${LogFilterWord.PaymentResult}`
		)

	const approved = (): void => {
		transitionLog.watchPaint({
			// `inAngularZone` is only meaningful before the zoneless upgrade.
			line: `Payment result · gateway ${gateway} · approved yes · zone ${yesNo(NgZone.isInAngularZone())}`,
			filterWord: LogFilterWord.PaymentResult,
			expected: PAYMENT_DIALOG_CLOSED,
			read: () => (host.isConnected ? PAYMENT_DIALOG_OPEN : null),
			receivedAt: performance.now(),
			injector,
		})
	}

	return { approved, declined }
}

/** One painted window: it logs once, on the first conclusive sample, and never after it was closed. */
class PaintWindow {
	readonly #watch: PaintWatch
	readonly #checks: readonly number[]
	readonly #settle: (outcome: PaintOutcome) => void
	readonly #pageVisible: () => boolean
	readonly #visibleAtReceipt: boolean

	#closed = false
	#timers: readonly ReturnType<typeof setTimeout>[] = []

	constructor(
		watch: PaintWatch,
		checks: readonly number[],
		pageVisible: () => boolean,
		settle: (outcome: PaintOutcome) => void
	) {
		this.#watch = watch
		this.#checks = checks
		this.#settle = settle
		this.#pageVisible = pageVisible
		this.#visibleAtReceipt = pageVisible()
	}

	schedule(): void {
		const lastCheck = this.#checks.length - 1

		this.#timers = this.#checks.map((check, index) =>
			setTimeout(
				() => this.sample({ final: index === lastCheck }),
				Math.max(0, this.#watch.receivedAt + check - performance.now())
			)
		)
	}

	/** A sample that is not the final one only concludes when the screen already shows the expected value. */
	sample({ final }: { readonly final: boolean } = { final: false }): void {
		try {
			if (this.#closed) return

			const { expected, read, receivedAt } = this.#watch
			const shown = read()
			const ms = Math.round(performance.now() - receivedAt)
			const visible = this.#visibleAtReceipt && this.#pageVisible()
			const verdict = paintVerdict(expected, shown, ms, visible)
			if (isUnpainted(verdict) && !final) return

			this.close()
			this.#settle({ verdict, shown, ms, visible })
		} catch {
			this.close()
		}
	}

	close(): void {
		this.#closed = true
		this.#timers.forEach(timer => clearTimeout(timer))
	}
}
